import gsap from 'gsap'
import { initDialog } from '../../components/dialog'

const PANEL_DURATION = 0.55
const ITEM_DURATION = 0.45
const TOGGLE_DURATION = 0.42
const EASE_PANEL = 'power3.inOut'
const EASE_ITEMS = 'power2.out'
const EASE_TOGGLE = 'power3.inOut'
const DESKTOP_MQ = '(min-width: 993px)'

const isDesktop = () => window.matchMedia(DESKTOP_MQ).matches

const usesHeightAnimation = (item) =>
	!isDesktop() || !item.closest('.site-header__nav--left')

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

let closeMobileNavRef = null

const initSubmenuState = (item) => {
	const submenu = item.querySelector(':scope > .sub-menu')

	if (!submenu || !usesHeightAnimation(item)) {
		return
	}

	const isOpen = item.classList.contains('is-submenu-open')

	if (reducedMotion()) {
		gsap.set(submenu, { height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 })
		return
	}

	gsap.set(submenu, {
		height: isOpen ? 'auto' : 0,
		opacity: isOpen ? 1 : 0,
		overflow: 'hidden',
	})
}

export const reinitHeaderNav = () => {
	document.querySelectorAll('.site-header .menu-item-has-children').forEach(initSubmenuState)
}

export const closeHeaderMobileNav = () => {
	closeMobileNavRef?.()
}

export const header = () => {
	const toggle = document.getElementById('nav-toggle')
	const mobilePanel = document.getElementById('header-mobile-nav')
	const searchDialog = document.getElementById('site-search')
	const headerEl = document.getElementById('header')
	const prefersReducedMotion = reducedMotion()

	let mobileOpen = false
	let mobileTimeline = null

	const getToggleBars = () => toggle?.querySelectorAll('.site-header__toggle-bar') ?? []

	const getMobileItems = () =>
		mobilePanel?.querySelectorAll('.site-header__menu--mobile > .menu-item') ?? []

	const setMobileOpen = (open) => {
		mobileOpen = open
		document.body.classList.toggle('header-active', open)

		if (toggle) {
			toggle.setAttribute('aria-expanded', open ? 'true' : 'false')
		}

		if (mobilePanel) {
			mobilePanel.setAttribute('aria-hidden', open ? 'false' : 'true')
		}

		document.body.style.overflow = open ? 'hidden' : ''
	}

	const animateToggleIcon = (open) => {
		const bars = getToggleBars()

		if (!bars.length || prefersReducedMotion) {
			return
		}

		const [barTop, barMid, barBottom] = bars

		gsap.killTweensOf(bars)

		if (open) {
			gsap
				.timeline({ defaults: { ease: EASE_TOGGLE, duration: TOGGLE_DURATION } })
				.to(barTop, { y: 6, rotation: 45 }, 0)
				.to(barMid, { opacity: 0, scaleX: 0, duration: 0.22, ease: 'power2.in' }, 0)
				.to(barBottom, { y: -6, rotation: -45 }, 0)
			return
		}

		gsap.to(bars, {
			y: 0,
			rotation: 0,
			opacity: 1,
			scaleX: 1,
			duration: TOGGLE_DURATION,
			ease: EASE_TOGGLE,
			stagger: 0.03,
		})
	}

	const resetMobilePanel = () => {
		if (!mobilePanel) {
			return
		}

		gsap.set(mobilePanel, { height: 0, overflow: 'hidden', clearProps: 'clipPath' })
		gsap.set(getMobileItems(), { clearProps: 'opacity,visibility,transform,y' })
	}

	const animateSubmenu = (item, open) => {
		const submenu = item.querySelector(':scope > .sub-menu')
		const button = item.querySelector(':scope > [data-submenu-toggle]')

		if (!submenu) {
			return
		}

		item.classList.toggle('is-submenu-open', open)

		if (button) {
			button.setAttribute('aria-expanded', open ? 'true' : 'false')
		}

		if (!usesHeightAnimation(item)) {
			return
		}

		if (prefersReducedMotion) {
			gsap.set(submenu, { height: open ? 'auto' : 0, opacity: open ? 1 : 0, clearProps: open ? 'height' : '' })
			return
		}

		gsap.killTweensOf(submenu)

		if (open) {
			gsap.set(submenu, { height: 'auto', opacity: 1 })
			const height = submenu.offsetHeight
			gsap.fromTo(
				submenu,
				{ height: 0, opacity: 0 },
				{ height, opacity: 1, duration: 0.5, ease: EASE_ITEMS, overflow: 'hidden' }
			)
			return
		}

		gsap.to(submenu, {
			height: 0,
			opacity: 0,
			duration: 0.4,
			ease: 'power2.in',
			overflow: 'hidden',
		})
	}

	const initSubmenus = () => {
		document.querySelectorAll('.site-header .menu-item-has-children').forEach(initSubmenuState)

		if (!headerEl) {
			return
		}

		headerEl.addEventListener('click', (event) => {
			const button = event.target.closest('[data-submenu-toggle]')

			if (!button || !headerEl.contains(button)) {
				return
			}

			event.preventDefault()

			const item = button.closest('.menu-item-has-children')

			if (!item) {
				return
			}

			const willOpen = !item.classList.contains('is-submenu-open')
			animateSubmenu(item, willOpen)
		})
	}

	const openMobileNav = () => {
		if (!mobilePanel) {
			setMobileOpen(true)
			return
		}

		mobileTimeline?.kill()
		setMobileOpen(true)
		animateToggleIcon(true)

		const items = getMobileItems()

		if (prefersReducedMotion) {
			gsap.set(mobilePanel, { height: 'auto', overflow: 'visible' })
			gsap.set(items, { autoAlpha: 1, y: 0 })
			return
		}

		gsap.set(mobilePanel, { height: 'auto', overflow: 'hidden' })
		const targetHeight = mobilePanel.offsetHeight

		gsap.set(mobilePanel, { height: 0 })
		gsap.set(items, { autoAlpha: 0, y: 14 })

		mobileTimeline = gsap
			.timeline()
			.to(mobilePanel, {
				height: targetHeight,
				duration: PANEL_DURATION,
				ease: EASE_PANEL,
			})
			.to(
				items,
				{
					autoAlpha: 1,
					y: 0,
					duration: ITEM_DURATION,
					stagger: 0.055,
					ease: EASE_ITEMS,
				},
				`-=${PANEL_DURATION * 0.35}`
			)
			.set(mobilePanel, { height: 'auto', overflow: 'hidden' })
	}

	const closeMobileNav = () => {
		if (!mobilePanel) {
			setMobileOpen(false)
			return
		}

		mobileTimeline?.kill()
		animateToggleIcon(false)

		const items = getMobileItems()

		if (prefersReducedMotion || !mobileOpen) {
			resetMobilePanel()
			setMobileOpen(false)
			return
		}

		const currentHeight = mobilePanel.offsetHeight

		gsap.set(mobilePanel, { height: currentHeight, overflow: 'hidden' })

		mobileTimeline = gsap.timeline({
			onComplete: () => {
				resetMobilePanel()
				setMobileOpen(false)
			},
		})

		mobileTimeline
			.to(items, {
				autoAlpha: 0,
				y: 8,
				duration: 0.28,
				stagger: { each: 0.035, from: 'end' },
				ease: 'power2.in',
			})
			.to(
				mobilePanel,
				{
					height: 0,
					duration: 0.48,
					ease: EASE_PANEL,
				},
				'-=0.1'
			)
	}

	closeMobileNavRef = closeMobileNav

	const handleToggle = () => {
		if (mobileOpen) {
			closeMobileNav()
			return
		}
		openMobileNav()
	}

	if (toggle) {
		toggle.addEventListener('click', handleToggle)
	}

	document.addEventListener('keydown', (event) => {
		if ('Escape' === event.key && mobileOpen) {
			closeMobileNav()
		}
	})

	initDialog({
		dialog: searchDialog,
		openSelector: '[data-search-open]',
		closeSelector: '[data-search-close]',
		focusSelector: '.site-search__input',
		onBeforeOpen: () => {
			if (mobileOpen) {
				closeMobileNav()
			}
		},
	})

	if (mobilePanel) {
		resetMobilePanel()
	}

	if (headerEl && !prefersReducedMotion) {
		gsap.from(headerEl, {
			y: -12,
			autoAlpha: 0,
			duration: 0.55,
			ease: 'power3.out',
			delay: 0.05,
		})
	}

	initSubmenus()
}
